import {
  type ChatAttachment as ContractChatAttachment,
  type EnvironmentId,
  isProviderDriverKind,
  type MessageId,
  ProjectId,
  type ModelSelection,
  type OrchestrationQueuedTurn,
  PRIMARY_LOCAL_ENVIRONMENT_ID,
  type ProviderDriverKind,
  type ServerProvider,
  type ScopedProjectRef,
  type ScopedThreadRef,
  type ThreadId,
  type TurnId,
  type UploadChatAttachment,
} from "@t3tools/contracts";
import { type ChatAttachment, type ChatMessage, type SessionPhase, type Thread } from "../types";
import { type ComposerAttachment, type DraftThreadState } from "../composerDraftStore";
import * as Schema from "effect/Schema";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { environmentThreadDetails } from "../state/threads";
import {
  filterTerminalContextsWithText,
  stripInlineTerminalContextPlaceholders,
  type TerminalContextDraft,
} from "../lib/terminalContext";
import type { DraftThreadEnvMode } from "../composerDraftStore";
import { i18n } from "../i18n";
import {
  orchestrationDispatchDisposition,
  orchestrationDispatchErrorCode,
} from "@t3tools/client-runtime/operations/dispatch-failure";

export const LAST_INVOKED_SCRIPT_BY_PROJECT_KEY = "t3code:last-invoked-script-by-project";
export const MAX_HIDDEN_MOUNTED_TERMINAL_THREADS = 10;
export const MAX_HIDDEN_MOUNTED_PREVIEW_THREADS = 3;

export const LastInvokedScriptByProjectSchema = Schema.Record(ProjectId, Schema.String);

export function dispatchFailureMessage(error: unknown): string {
  if (orchestrationDispatchErrorCode(error) === "unsupported-image-mime-type") {
    return i18n.t("chat.nativeImageMimeUnsupported");
  }
  switch (orchestrationDispatchDisposition(error)) {
    case "send-outcome-unknown":
      return i18n.t("chat.sendOutcomeUnknown");
    case "history-unavailable":
      return i18n.t("chat.historyUnavailable");
    case "provider-unavailable":
      return i18n.t("chat.providerUnavailable");
    case "send-failed":
    default:
      return error instanceof Error ? error.message : i18n.t("chat.sendFailed");
  }
}

export function deriveCheckpointRevertTurnCounts(input: {
  readonly messages: ReadonlyArray<Pick<ChatMessage, "id" | "role">>;
  readonly checkpointTurnCountByAssistantMessageId: ReadonlyMap<MessageId, number>;
}): Map<MessageId, number> {
  const result = new Map<MessageId, number>();
  for (let index = 0; index < input.messages.length; index += 1) {
    const message = input.messages[index];
    if (!message || message.role !== "user") continue;
    for (let nextIndex = index + 1; nextIndex < input.messages.length; nextIndex += 1) {
      const nextMessage = input.messages[nextIndex];
      if (!nextMessage || nextMessage.role === "user") break;
      if (nextMessage.role !== "assistant") continue;
      const checkpointTurnCount = input.checkpointTurnCountByAssistantMessageId.get(nextMessage.id);
      if (checkpointTurnCount === undefined) continue;
      result.set(message.id, Math.max(0, checkpointTurnCount - 1));
      break;
    }
  }
  return result;
}

export function isQueuedTurnEditTargetCurrent(input: {
  readonly editThreadKey: string;
  readonly currentThreadKey: string;
  readonly messageId: string;
  readonly queuedTurns: ReadonlyArray<OrchestrationQueuedTurn> | undefined;
}): boolean {
  return (
    input.editThreadKey === input.currentThreadKey &&
    input.queuedTurns?.some(
      (turn) => turn.messageId === input.messageId && turn.status === "queued",
    ) === true
  );
}

export function disposableQueuedTurnEditBlobUrls(input: {
  readonly editImages: ReadonlyArray<Pick<ComposerAttachment, "previewUrl">>;
  readonly backupImages: ReadonlyArray<Pick<ComposerAttachment, "previewUrl">>;
}): ReadonlyArray<string> {
  const backupUrls = new Set(input.backupImages.map((image) => image.previewUrl));
  return input.editImages
    .map((image) => image.previewUrl)
    .filter(
      (url): url is string =>
        typeof url === "string" && url.startsWith("blob:") && !backupUrls.has(url),
    );
}

export function startNewThreadForProject(
  projectRef: ScopedProjectRef | null,
  handleNewThread: (projectRef: ScopedProjectRef) => Promise<void>,
): boolean {
  if (projectRef === null) return false;
  void handleNewThread(projectRef);

  return true;
}

export function resolveThreadMetadataUpdateForNextTurn(input: {
  currentModelSelection: ModelSelection;
  nextModelSelection?: ModelSelection;
  currentBranch: string | null;
  nextBranch?: string;
}): {
  modelSelection?: ModelSelection;
  branch?: string;
  worktreePath?: null;
} | null {
  const nextModelSelection = input.nextModelSelection;
  const modelSelectionChanged =
    nextModelSelection !== undefined &&
    (nextModelSelection.model !== input.currentModelSelection.model ||
      nextModelSelection.instanceId !== input.currentModelSelection.instanceId ||
      JSON.stringify(nextModelSelection.options ?? null) !==
        JSON.stringify(input.currentModelSelection.options ?? null));
  const branchChanged = input.nextBranch !== undefined && input.nextBranch !== input.currentBranch;
  if (!modelSelectionChanged && !branchChanged) {
    return null;
  }
  return {
    ...(modelSelectionChanged ? { modelSelection: nextModelSelection } : {}),
    ...(branchChanged ? { branch: input.nextBranch, worktreePath: null } : {}),
  };
}

export function resolveEnqueueAdmissionPolicy(input: {
  isServerThread: boolean;
  sessionStatus: NonNullable<Thread["session"]>["status"] | null;
  hasQueuedTurns: boolean;
}): {
  persistTurnModes: boolean;
} {
  const hasActiveTurn = input.sessionStatus === "starting" || input.sessionStatus === "running";
  return {
    persistTurnModes: input.isServerThread && !hasActiveTurn && !input.hasQueuedTurns,
  };
}

export function buildLocalDraftThread(
  threadId: ThreadId,
  draftThread: DraftThreadState,
  fallbackModelSelection: ModelSelection,
): Thread {
  return {
    id: threadId,
    environmentId: draftThread.environmentId,
    projectId: draftThread.projectId,
    title: i18n.t("sidebar.newThread"),
    modelSelection: fallbackModelSelection,
    runtimeMode: draftThread.runtimeMode,
    interactionMode: draftThread.interactionMode,
    bindingState: "unbound",
    session: null,
    messages: [],
    createdAt: draftThread.createdAt,
    updatedAt: draftThread.createdAt,
    archivedAt: null,
    settledOverride: null,
    settledAt: null,
    deletedAt: null,
    latestTurn: null,
    branch: draftThread.branch,
    worktreePath: draftThread.worktreePath,
    checkpoints: [],
    activities: [],
    proposedPlans: [],
    queuedTurns: [],
  };
}

export function shouldWriteThreadErrorToCurrentServerThread(input: {
  serverThread:
    | {
        environmentId: EnvironmentId;
        id: ThreadId;
      }
    | null
    | undefined;
  routeThreadRef: ScopedThreadRef;
  targetThreadId: ThreadId;
}): boolean {
  return Boolean(
    input.serverThread &&
    input.targetThreadId === input.routeThreadRef.threadId &&
    input.serverThread.environmentId === input.routeThreadRef.environmentId &&
    input.serverThread.id === input.targetThreadId,
  );
}

export function buildThreadTurnInterruptInput(thread: Pick<Thread, "id" | "session">): {
  threadId: ThreadId;
  turnId?: TurnId;
} {
  const runningTurnId = thread.session?.status === "running" ? thread.session.activeTurnId : null;
  return {
    threadId: thread.id,
    ...(runningTurnId !== null ? { turnId: runningTurnId } : {}),
  };
}

type ThreadErrorSession = Pick<NonNullable<Thread["session"]>, "lastError">;

export function resolveVisibleThreadError(input: {
  localError: string | null;
  session: ThreadErrorSession | null | undefined;
  dismissedSessionError: string | null;
}): string | null {
  if (input.localError !== null) {
    return input.localError;
  }
  const sessionError = input.session?.lastError ?? null;
  return sessionError === input.dismissedSessionError ? null : sessionError;
}

export function reconcileMountedTerminalThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadTerminalOpen: boolean;
  maxHiddenThreadCount?: number;
}): string[] {
  return reconcileRetainedMountedThreadIds({
    currentThreadIds: input.currentThreadIds,
    openThreadIds: input.openThreadIds,
    activeThreadId: input.activeThreadId,
    activeThreadOpen: input.activeThreadTerminalOpen,
    maxHiddenThreadCount: input.maxHiddenThreadCount ?? MAX_HIDDEN_MOUNTED_TERMINAL_THREADS,
  });
}

export function reconcileRetainedMountedThreadIds(input: {
  currentThreadIds: ReadonlyArray<string>;
  openThreadIds: ReadonlyArray<string>;
  activeThreadId: string | null;
  activeThreadOpen: boolean;
  maxHiddenThreadCount: number;
  retainInactiveActiveThread?: boolean;
}): string[] {
  const openThreadIdSet = new Set(input.openThreadIds);
  const hiddenThreadIds = input.currentThreadIds.filter(
    (threadId) =>
      (threadId !== input.activeThreadId || input.retainInactiveActiveThread === true) &&
      openThreadIdSet.has(threadId),
  );
  const maxHiddenThreadCount = Math.max(0, input.maxHiddenThreadCount);
  const nextThreadIds =
    hiddenThreadIds.length > maxHiddenThreadCount
      ? hiddenThreadIds.slice(-maxHiddenThreadCount)
      : hiddenThreadIds;

  if (
    input.activeThreadId &&
    input.activeThreadOpen &&
    !nextThreadIds.includes(input.activeThreadId)
  ) {
    nextThreadIds.push(input.activeThreadId);
  }

  return nextThreadIds;
}

export function revokeBlobPreviewUrl(previewUrl: string | undefined): void {
  if (!previewUrl || typeof URL === "undefined" || !previewUrl.startsWith("blob:")) {
    return;
  }
  URL.revokeObjectURL(previewUrl);
}

export function revokeUserMessagePreviewUrls(message: ChatMessage): void {
  if (message.role !== "user" || !message.attachments) {
    return;
  }
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") {
      continue;
    }
    revokeBlobPreviewUrl(attachment.previewUrl);
  }
}

export function collectUserMessageBlobPreviewUrls(message: ChatMessage): string[] {
  if (message.role !== "user" || !message.attachments) {
    return [];
  }
  const previewUrls: string[] = [];
  for (const attachment of message.attachments) {
    if (attachment.type !== "image") continue;
    if (!attachment.previewUrl || !attachment.previewUrl.startsWith("blob:")) continue;
    previewUrls.push(attachment.previewUrl);
  }
  return previewUrls;
}

export interface PullRequestDialogState {
  initialReference: string | null;
  key: number;
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Could not read image data."));
    });
    reader.addEventListener("error", () => {
      reject(reader.error ?? new Error("Failed to read image."));
    });
    reader.readAsDataURL(file);
  });
}

export function resolveSendEnvMode(input: {
  requestedEnvMode: DraftThreadEnvMode;
  isGitRepo: boolean;
}): DraftThreadEnvMode {
  return input.isGitRepo ? input.requestedEnvMode : "local";
}

export interface RunContextControllerSendState {
  readonly effectiveEnvMode: DraftThreadEnvMode;
  readonly activeThreadBranch: string | null;
  readonly startFromOrigin: boolean;
}

export function resolveRunContextSendState(input: {
  readonly controllerState: RunContextControllerSendState;
  readonly isGitRepo: boolean;
  readonly isFirstMessage: boolean;
  readonly activeWorktreePath: string | null;
}) {
  const sendEnvMode = resolveSendEnvMode({
    requestedEnvMode: input.controllerState.effectiveEnvMode,
    isGitRepo: input.isGitRepo,
  });
  return {
    sendEnvMode,
    baseBranchForWorktree:
      input.isFirstMessage && sendEnvMode === "worktree" && input.activeWorktreePath === null
        ? input.controllerState.activeThreadBranch
        : null,
    startFromOrigin: input.controllerState.startFromOrigin,
  };
}

export function cloneComposerImageForRetry(image: ComposerAttachment): ComposerAttachment {
  return image;
}

export function canUseSharedFilesystemAttachments(
  environmentId: EnvironmentId,
  attachmentIngress: "shared-filesystem" | "inline" | undefined,
): boolean {
  return (
    environmentId === PRIMARY_LOCAL_ENVIRONMENT_ID && attachmentIngress === "shared-filesystem"
  );
}

export function composerAttachmentForUpload(
  attachment: ComposerAttachment,
  options: { readonly allowLocalFileAttachments?: boolean } = {},
): UploadChatAttachment {
  const common = {
    type: "image" as const,
    name: attachment.name,
    mimeType: attachment.mimeType,
    sizeBytes: attachment.sizeBytes,
  };
  const protocol = new URL(attachment.uri).protocol;
  if (protocol === "file:") {
    if (options.allowLocalFileAttachments !== true) {
      throw new Error("Local file attachments are unavailable for this environment.");
    }
    return { ...common, sha256: attachment.sha256, uri: attachment.uri };
  }
  if (protocol === "data:") {
    return { ...common, dataUrl: attachment.uri };
  }
  throw new Error(`Unsupported composer attachment URI protocol: ${protocol}`);
}

export function displayChatAttachments(
  attachments: ReadonlyArray<ContractChatAttachment>,
  previewUrlById: ReadonlyMap<string, string>,
): ChatAttachment[] {
  return attachments.map((attachment) => {
    if (!("id" in attachment)) return attachment;
    const previewUrl = previewUrlById.get(attachment.id);
    return previewUrl ? { ...attachment, previewUrl } : attachment;
  });
}

export function localQueuedAttachmentForComposer(
  attachment: Extract<ContractChatAttachment, { readonly uri: string }>,
  draftKey: string,
): ComposerAttachment {
  return {
    draftKey,
    name: attachment.name,
    mimeType: attachment.mimeType,
    sizeBytes: attachment.sizeBytes,
    sha256: attachment.sha256,
    uri: attachment.uri,
  };
}

export async function sha256HexForBlob(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function deriveComposerSendState(options: {
  prompt: string;
  imageCount: number;
  terminalContexts: ReadonlyArray<TerminalContextDraft>;
  /**
   * Optional element-pick attachment count. Element contexts contribute to
   * "sendable content" exactly like images and (text-bearing) terminal
   * contexts do: a prompt of just element chips is still a valid send.
   */
  elementContextCount?: number;
}): {
  trimmedPrompt: string;
  sendableTerminalContexts: TerminalContextDraft[];
  expiredTerminalContextCount: number;
  hasSendableContent: boolean;
} {
  const trimmedPrompt = stripInlineTerminalContextPlaceholders(options.prompt).trim();
  const sendableTerminalContexts = filterTerminalContextsWithText(options.terminalContexts);
  const expiredTerminalContextCount =
    options.terminalContexts.length - sendableTerminalContexts.length;
  const elementContextCount = options.elementContextCount ?? 0;
  return {
    trimmedPrompt,
    sendableTerminalContexts,
    expiredTerminalContextCount,
    hasSendableContent:
      trimmedPrompt.length > 0 ||
      options.imageCount > 0 ||
      sendableTerminalContexts.length > 0 ||
      elementContextCount > 0,
  };
}

export function buildExpiredTerminalContextToastCopy(
  expiredTerminalContextCount: number,
  variant: "omitted" | "empty",
): { title: string; description: string } {
  const count = Math.max(1, Math.floor(expiredTerminalContextCount));
  const noun = count === 1 ? "Expired terminal context" : "Expired terminal contexts";
  if (variant === "empty") {
    return {
      title: `${noun} won't be sent`,
      description: "Remove it or re-add it to include terminal output.",
    };
  }
  return {
    title: `${noun} omitted from message`,
    description: "Re-add it if you want that terminal output included.",
  };
}

export function branchMismatchKey(
  threadId: string | null,
  mismatch: { threadBranch: string; currentBranch: string } | null,
): string | null {
  if (!threadId || !mismatch) {
    return null;
  }
  return `${threadId}:${mismatch.threadBranch}:${mismatch.currentBranch}`;
}

// The mismatch banner only matters when the user is about to send: passive
// reading of an old thread carries no risk (the branch picker tint already
// covers ambient awareness). Draft content is the intent signal — composer
// focus is useless here because ChatView autofocuses the composer on every
// thread open. `wasShownForCurrentMismatch` keeps the banner mounted once
// revealed so it doesn't flicker away when the draft is cleared.
export function shouldShowBranchMismatchBanner(input: {
  hasMismatch: boolean;
  isDismissed: boolean;
  composerHasContent: boolean;
  wasShownForCurrentMismatch: boolean;
}): boolean {
  if (!input.hasMismatch || input.isDismissed) {
    return false;
  }
  return input.composerHasContent || input.wasShownForCurrentMismatch;
}

// Session-scoped (module-level so it survives ChatView remounts, e.g. route
// changes). Durable cross-device dismissal is planned as a server-side ack.
const sessionDismissedBranchMismatchKeys = new Set<string>();

export function dismissBranchMismatchForSession(key: string): void {
  sessionDismissedBranchMismatchKeys.add(key);
}

export function isBranchMismatchDismissedForSession(key: string | null): boolean {
  return key !== null && sessionDismissedBranchMismatchKeys.has(key);
}

export function threadHasStarted(thread: Thread | null | undefined): boolean {
  // Draft promotion is an acceptance proof, not a runtime liveness check.
  // Session/latestTurn/work can race ahead of the Provider's canonical user row.
  return Boolean(thread?.messages.some((message) => message.role === "user"));
}

export interface PendingDraftSubmissionIdentity {
  readonly submissionId: MessageId;
  readonly timelineMessageId: MessageId | null;
}

export function reconcilePendingDraftSubmissionIdentity(input: {
  readonly pending: PendingDraftSubmissionIdentity;
  readonly session: {
    readonly activeSubmissionId?: MessageId | null | undefined;
    readonly activeUserMessageId?: MessageId | null | undefined;
  } | null;
  readonly messages: ReadonlyArray<Pick<ChatMessage, "id" | "role">>;
}): {
  readonly pending: PendingDraftSubmissionIdentity;
  readonly acknowledged: boolean;
} {
  const timelineMessageId =
    input.session?.activeSubmissionId === input.pending.submissionId &&
    input.session.activeUserMessageId != null
      ? input.session.activeUserMessageId
      : input.pending.timelineMessageId;
  return {
    pending: { ...input.pending, timelineMessageId },
    acknowledged:
      timelineMessageId !== null &&
      input.messages.some((message) => message.role === "user" && message.id === timelineMessageId),
  };
}

export function shouldAllowComposerProviderSelection(thread: Thread | null | undefined): boolean {
  return !threadBindingLocksProvider(thread) && !threadHasStarted(thread);
}

export function threadBindingBlocksComposerSend(
  thread: Pick<Thread, "bindingState"> | null | undefined,
): boolean {
  return thread?.bindingState === "connecting" || thread?.bindingState === "deleting";
}

export function shouldClearPlanFollowUpDraft(submitted: boolean): boolean {
  return submitted;
}

export function shouldOpenCreatedImplementationThread(createSucceeded: boolean): boolean {
  return createSucceeded;
}

function threadBindingLocksProvider(thread: Thread | null | undefined): boolean {
  return thread !== null && thread !== undefined && thread.bindingState !== "unbound";
}

// `threadProvider` is the open branded driver kind carried by the session.
// Unknown driver kinds degrade to `null` (i.e. "unlocked"), which is the safe
// rollback / fork behavior — the routing layer is the right place to surface
// "driver not installed" errors, not the lock state.
//
// `selectedProvider` takes the same open-string shape because the composer
// now tracks the picker selection as a `ProviderInstanceId` (e.g.
// `codex_personal`). Custom instance ids that don't directly match a
// registered driver resolve to `null` here, which matches the existing
// "unknown driver -> unlocked" semantics. Callers that want the lock to track
// a custom instance's underlying driver kind should resolve the instance id
// upstream and pass the correlated kind.
export function deriveLockedProvider(input: {
  thread: Thread | null | undefined;
  selectedProvider: string | null;
  threadProvider: string | null;
}): ProviderDriverKind | null {
  if (!threadBindingLocksProvider(input.thread) && !threadHasStarted(input.thread)) {
    return null;
  }
  const sessionProvider = input.thread?.session?.providerName ?? null;
  if (sessionProvider && isProviderDriverKind(sessionProvider)) {
    return sessionProvider;
  }
  const narrowedThreadProvider =
    input.threadProvider && isProviderDriverKind(input.threadProvider)
      ? input.threadProvider
      : null;
  const narrowedSelectedProvider =
    input.selectedProvider && isProviderDriverKind(input.selectedProvider)
      ? input.selectedProvider
      : null;
  return narrowedThreadProvider ?? narrowedSelectedProvider ?? null;
}

export function getStartedThreadModelChangeBlockReason(input: {
  providers: ReadonlyArray<Pick<ServerProvider, "instanceId" | "requiresNewThreadForModelChange">>;
  hasStartedSession: boolean;
  currentModelSelection: ModelSelection;
  currentProviderInstanceId?: ModelSelection["instanceId"] | null | undefined;
  nextModelSelection: ModelSelection;
}): { title: string; description: string } | null {
  if (!input.hasStartedSession) {
    return null;
  }
  const currentModelSelection = {
    ...input.currentModelSelection,
    instanceId: input.currentProviderInstanceId ?? input.currentModelSelection.instanceId,
  };
  if (
    currentModelSelection.instanceId === input.nextModelSelection.instanceId &&
    currentModelSelection.model === input.nextModelSelection.model
  ) {
    return null;
  }
  const currentProvider = input.providers.find(
    (snapshot) => snapshot.instanceId === currentModelSelection.instanceId,
  );
  const nextProvider = input.providers.find(
    (snapshot) => snapshot.instanceId === input.nextModelSelection.instanceId,
  );
  if (
    currentProvider?.requiresNewThreadForModelChange !== true &&
    nextProvider?.requiresNewThreadForModelChange !== true
  ) {
    return null;
  }
  return {
    title: "Start a new chat to change models",
    description: "This provider does not allow switching models after a conversation has started.",
  };
}

export async function waitForStartedServerThread(
  threadRef: ScopedThreadRef,
  timeoutMs = 1_000,
): Promise<boolean> {
  const threadAtom = environmentThreadDetails.detailAtom(threadRef);
  const getThread = () => appAtomRegistry.get(threadAtom);
  const thread = getThread();

  if (threadHasStarted(thread)) {
    return true;
  }

  return await new Promise<boolean>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
    const finish = (result: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
      unsubscribe();
      resolve(result);
    };

    const unsubscribe = appAtomRegistry.subscribe(threadAtom, (thread) => {
      if (!threadHasStarted(thread)) {
        return;
      }
      finish(true);
    });

    if (threadHasStarted(getThread())) {
      finish(true);
      return;
    }

    timeoutId = globalThis.setTimeout(() => {
      finish(false);
    }, timeoutMs);
  });
}

export interface LocalDispatchSnapshot {
  startedAt: string;
  preparingWorktree: boolean;
  latestUserMessageId: ChatMessage["id"] | null;
  latestTurnTurnId: TurnId | null;
  latestTurnRequestedAt: string | null;
  latestTurnStartedAt: string | null;
  latestTurnCompletedAt: string | null;
  sessionStatus: NonNullable<Thread["session"]>["status"] | null;
  sessionUpdatedAt: string | null;
}

export function createLocalDispatchSnapshot(
  activeThread: Thread | undefined,
  options?: { preparingWorktree?: boolean },
): LocalDispatchSnapshot {
  const latestTurn = activeThread?.latestTurn ?? null;
  const session = activeThread?.session ?? null;
  const latestUserMessage = activeThread?.messages.findLast((message) => message.role === "user");
  return {
    startedAt: new Date().toISOString(),
    preparingWorktree: Boolean(options?.preparingWorktree),
    latestUserMessageId: latestUserMessage?.id ?? null,
    latestTurnTurnId: latestTurn?.turnId ?? null,
    latestTurnRequestedAt: latestTurn?.requestedAt ?? null,
    latestTurnStartedAt: latestTurn?.startedAt ?? null,
    latestTurnCompletedAt: latestTurn?.completedAt ?? null,
    sessionStatus: session?.status ?? null,
    sessionUpdatedAt: session?.updatedAt ?? null,
  };
}

export function shouldShowWorkingStatus(input: {
  phase: SessionPhase;
  isSendBusy: boolean;
  isConnecting: boolean;
  isRevertingCheckpoint: boolean;
}): boolean {
  return (
    input.phase === "connecting" ||
    input.phase === "running" ||
    input.isSendBusy ||
    input.isConnecting ||
    input.isRevertingCheckpoint
  );
}

export function resolveWorkingStatusLabel(input: {
  phase: SessionPhase;
  isSendBusy: boolean;
  isConnecting: boolean;
  providerWorkingStatus: string | null;
  connectingLabel: string;
}): string | null {
  if (input.phase === "running") {
    return input.providerWorkingStatus;
  }
  if (input.phase === "connecting" || input.isConnecting || input.isSendBusy) {
    return input.connectingLabel;
  }
  return null;
}

export function hasServerAcknowledgedLocalDispatch(input: {
  localDispatch: LocalDispatchSnapshot | null;
  phase: SessionPhase;
  latestTurn: Thread["latestTurn"] | null;
  latestUserMessageId: ChatMessage["id"] | null;
  session: Thread["session"] | null;
  hasPendingApproval: boolean;
  hasPendingUserInput: boolean;
  threadError: string | null | undefined;
}): boolean {
  if (!input.localDispatch) {
    return false;
  }
  if (input.hasPendingApproval || input.hasPendingUserInput || Boolean(input.threadError)) {
    return true;
  }

  const latestTurn = input.latestTurn ?? null;
  const session = input.session ?? null;
  const latestUserMessageChanged =
    input.localDispatch.latestUserMessageId !== input.latestUserMessageId;
  const latestTurnChanged =
    input.localDispatch.latestTurnTurnId !== (latestTurn?.turnId ?? null) ||
    input.localDispatch.latestTurnRequestedAt !== (latestTurn?.requestedAt ?? null) ||
    input.localDispatch.latestTurnStartedAt !== (latestTurn?.startedAt ?? null) ||
    input.localDispatch.latestTurnCompletedAt !== (latestTurn?.completedAt ?? null);

  if (input.phase === "running") {
    // Steering adds a user message to the current running turn without
    // necessarily changing any of the turn timestamps. Treat that projected
    // message as the server acknowledgment so the composer does not remain
    // stuck in its local "Sending" state until the turn settles.
    if (latestUserMessageChanged) {
      return true;
    }
    if (!latestTurnChanged) {
      return false;
    }
    if (latestTurn?.startedAt === null || latestTurn === null) {
      return false;
    }
    if (
      session?.activeTurnId !== null &&
      session?.activeTurnId !== undefined &&
      latestTurn?.turnId !== session.activeTurnId
    ) {
      return false;
    }
    return true;
  }

  return (
    latestTurnChanged ||
    input.localDispatch.sessionStatus !== (session?.status ?? null) ||
    input.localDispatch.sessionUpdatedAt !== (session?.updatedAt ?? null)
  );
}
